<template>
  <RevPortal>
    <Transition
      appear
      enter-active-class="motion-safe:transition-opacity motion-safe:duration-300"
      enter-from-class="opacity-0"
      leave-active-class="motion-safe:transition-opacity motion-safe:duration-200"
      leave-to-class="opacity-0"
    >
      <div aria-live="polite" role="status">
        <div
          v-if="opened"
          ref="overlayElement"
          class="bg-surface-default-hi fixed inset-0 z-10 overflow-y-auto"
          role="dialog"
        >
          <Transition
            appear
            enter-active-class="motion-safe:transition-transform motion-safe:duration-300 md:motion-safe:transition-opacity"
            enter-from-class="translate-y-full md:opacity-0"
            leave-active-class="motion-safe:transition-transform motion-safe:duration-200 md:motion-safe:transition-opacity"
            leave-to-class="translate-y-full md:opacity-0"
          >
            <div>
              <ItemsConfirmation
                class="bg-surface-default-low"
                :items="allItems"
                :trade-in
              />

              <RevLoadingScreen v-if="loading" class="h-[90vh]" text="" />

              <Recommendations
                v-show="!loading"
                class="m-24 pb-64 md:pb-96"
                :listing-id="items[0].listingId"
                @added-to-cart="addProduct"
                @back="onRecommendationsBackClick"
                @go-to-cart="goToCart"
                @loaded="onRecommendationsLoaded"
                @no-recommendation="onNoRecommendations"
              />
            </div>
          </Transition>
        </div>
      </div>
    </Transition>
  </RevPortal>
</template>

<script lang="ts" setup>
import { navigateTo } from '#imports'
import { computed, ref, useTemplateRef, watch } from 'vue'

import type { Product } from '@backmarket/nuxt-layer-recommendation/models/product'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevLoadingScreen } from '@ds/components/LoadingScreen'
import { RevPortal } from '@ds/components/Portal'

import { CHECKOUT } from '~/scopes/checkout/routes-names'

import ItemsConfirmation from './components/ItemsConfirmation/ItemsConfirmation.vue'
import Recommendations from './components/Recommendations/Recommendations.vue'

const props = defineProps<{
  items: Array<{
    listingId: number
    image: string
    title: string
    quantity: number
  }>
  tradeIn: string | null
  opened: boolean
}>()

const emit = defineEmits(['close'])
const { trackModal } = useTracking()
const locale = useI18nLocale()
const overlayElement = useTemplateRef<HTMLDivElement>('overlayElement')

const isRecommendationsStepLoading = ref(true)
const addedRecommendations = ref<
  Array<{
    listingId: number
    image: string
    title: string
    quantity: number
  }>
>([])

const allItems = computed(() => [...props.items, ...addedRecommendations.value])
const hasRecommendations = ref(true)

const loading = computed(() => {
  return isRecommendationsStepLoading.value
})

async function goToCart() {
  await navigateTo({
    name: CHECKOUT.CART,
    params: { locale },
  })
}

function addProduct(product: Product) {
  addedRecommendations.value.push({
    listingId: parseInt(product.listingId, 10),
    image: product.image,
    title: `${product.title} ${product.description}`,
    quantity: 1,
  })
}

function onRecommendationsLoaded() {
  isRecommendationsStepLoading.value = false
}

function onNoRecommendations() {
  hasRecommendations.value = false
  isRecommendationsStepLoading.value = false

  goToCart()
}

function onRecommendationsBackClick() {
  emit('close')
}

function track() {
  trackModal({ name: 'ADD_TO_CART_RECO', status: 'opened' })
}

watch(
  () => props.opened,
  () => {
    // No automatic tracking here since we're not using a modal
    if (props.opened) {
      track()
    }
  },
)
</script>
